// SEND-259 (Howard ruled 2026-08-14) — SIDING COLOR PICKER ON THE PHOTO RAIL.
// SEND-260 (Howard ruled 2026-08-14) — B&B AND SHAKE COLOR, SAME PATTERN.
// SEND-262 (Howard ruled 2026-08-14) — ACCESSORIES COLOR ON J, CORNERS AND
// FINISH TRIM, same pattern again: "The pick writes ACCESSORIES COLOR on this
// estimate. ONE FIELD. ALL FOUR RAILS WRITE THE SAME FIELD."
//
//   "Do not hardcode color names. Read them from the same catalog the estimate
//   page uses. If a product has no Arch list, Arch is empty and says so. DO
//   NOT INVENT COLORS. DO NOT BORROW siding, B&B, or shake colors."
//
// So this file declares NO COLOR NAME. Every name it renders comes out of
// /app/frontend/src/lib/colorOptions.js — the very module the estimate page's
// own dropdowns read — and the Standard / Architectural split is read off
// THOSE GROUPS' OWN LABELS, so the rail can never disagree with the page.
//
// EACH MATERIAL HAS ITS OWN PALETTE AND ITS OWN ESTIMATE FIELD. Vinyl lap goes
// to SIDING COLOR, Vertical B&B to BOARD & BATTEN COLOR, Shakes to SHAKE
// COLOR, and J / outside corner / inside corner / finish trim all go to the
// ONE ACCESSORIES COLOR. No material is shown another material's list, and no
// field is written by a rail that does not own it.
//
// Ascend, window wrap, gutter and soffit are NOT in this send: the Ascend
// optgroup the estimate page appends to its accessories list is left out here
// BY NAME (see `accessoryGroups`), and nothing reads or writes the others.
import React from "react";
import { VINYL_BRAND_COLOR_GROUPS } from "@/lib/colorOptions";
// Read as separate statements from the SAME module on purpose: SEND-259's pin
// names the line above by hand, and each send adds to it rather than rewriting
// what he already signed off.
import { BOARD_BATTEN_COLOR_GROUPS, SHAKE_COLOR_GROUPS } from "@/lib/colorOptions";
import { accessoryColorGroupsForEstimate } from "@/lib/colorOptions";
import { SOFFIT_COLOR_GROUPS } from "@/lib/colorOptions";
// SEND-272 — the OTHER palettes the two J steps read, each the product's own.
import { BOARD_BATTEN_COLOR_GROUPS as BB_GROUPS,
  SHAKE_COLOR_GROUPS as SHAKE_GROUPS,
  vinylSidingColorGroupsForEstimate } from "@/lib/colorOptions";
// SEND-270 — the same module again, this time for the PER-PRODUCT soffit
// collections the walk's soffit step filters by.
import { SOFFIT_COLOR_GROUPS_BY_SERIES } from "@/lib/colorOptions";

// The photo rail's PRODUCT key → the estimate page's vinyl brand key. Charter
// Oak is `charter_oak` on the rail and `charter` in the catalog; the other
// three already match. Vertical B&B and Shakes are NOT vinyl brands — they
// have their own palettes below.
export const BRAND_FOR_SERIES = {
  conquest: "conquest",
  coventry: "coventry",
  odyssey: "odyssey",
  charter_oak: "charter",
};

// SEND-260 — THE PRODUCT'S OWN FIELD ON THE ESTIMATE. The same fields the
// estimate page's Material Colors dropdowns bind to, one per material family.
export const COLOR_FIELD_FOR_SERIES = {
  conquest: "siding_color",
  coventry: "siding_color",
  odyssey: "siding_color",
  charter_oak: "siding_color",
  vertical_bb: "board_batten_color",
  shakes: "shake_color",
};

export const COLOR_LABEL_FOR_FIELD = {
  siding_color: "Siding color",
  board_batten_color: "Board & batten color",
  shake_color: "Shake color",
  accessories_color: "Accessories color",
  soffit_fascia_color: "Soffit & fascia color",
  gutter_color: "Gutter color",
};

// SEND-264 (Howard ruled 2026-08-14) — COLOR FOLLOWS THE PRODUCT, AND THE
// OUTSIDE CORNER IS THE EXCEPTION.
//
//   "Siding J → SIDING COLOR. Inside corners → SIDING COLOR. Finish trim →
//   SIDING COLOR. B&B J → BOARD & BATTEN COLOR. Shake J → SHAKE COLOR.
//   OUTSIDE CORNERS → ACCESSORIES COLOR, own pick, often white. HIDE the
//   accessories color row on J, inside corner and finish trim: those rails do
//   not write ACCESSORIES COLOR. KEEP the color row on OUTSIDE CORNER only."
//
// Those first five already take the colour of the product they follow, chosen
// once on that product's own rail — so they get no second pick here and no
// field of their own. ONE RAIL keeps the accessories pick.
export const ACCESSORY_FIELD = "accessories_color";
export const ACCESSORY_TOOLS = ["outside_corner"];

// SEND-264 — THE ONE EXCEPTION ON THE J RAIL. He ruled soffit J onto the
// estimate's SOFFIT & FASCIA COLOR — the field DOES exist
// (`soffit_fascia_color`, the estimate page's own SOFFIT & FASCIA COLOR
// dropdown reading SOFFIT_COLOR_GROUPS); there is no soffit-only field and none
// was invented. Siding J, B&B J and shake J get NO colour row.
export const SOFFIT_J_FIELD = "soffit_fascia_color";
export const SOFFIT_J_KEY = "soffit";

// SEND-260 — the product's OWN groups. Vertical B&B reads the Board & Batten
// palette, Shakes reads the shake palette, and neither one borrows the vinyl
// lap colors.
const groupsForSeries = (seriesKey) => {
  if (seriesKey === "vertical_bb") return BOARD_BATTEN_COLOR_GROUPS;
  if (seriesKey === "shakes") return SHAKE_COLOR_GROUPS;
  return VINYL_BRAND_COLOR_GROUPS[BRAND_FOR_SERIES[seriesKey]] || [];
};

export const hasColorList = (seriesKey) => groupsForSeries(seriesKey).length > 0;

// The tier FILTERS the list and does nothing else — it picks no color, writes
// no money and renames nothing.
export const colorsFor = (seriesKey, tier) => {
  const wantArch = tier === "architectural";
  return groupsForSeries(seriesKey)
    .filter((g) => /architectural/i.test(g.label || "") === wantArch)
    .flatMap((g) => g.colors || []);
};

// SEND-262 — THE ACCESSORIES LIST IS THE ESTIMATE PAGE'S OWN, read through the
// same function its ACCESSORIES COLOR dropdown calls and narrowed by THIS
// ESTIMATE'S OWN LINES, so the rail and the page cannot disagree. The Ascend
// optgroup that function appends is LEFT OUT here by name — "do not add Ascend
// in this send" — and nothing else is added, removed or renamed.
export const accessoryGroups = (lines) =>
  accessoryColorGroupsForEstimate(lines || [])
    .filter((g) => !/^ascend$/i.test((g.label || "").trim()));

export const accessoryColorsFor = (tier, lines) => {
  const wantArch = tier === "architectural";
  // One NAME once: the page's accessories catalog concatenates every active
  // brand's collections, so the same color can appear in several of them. The
  // set of names is identical — a name is simply not listed twice.
  return [...new Set(accessoryGroups(lines)
    .filter((g) => /architectural/i.test(g.label || "") === wantArch)
    .flatMap((g) => g.colors || []))];
};

const NOTE = "text-[10px] font-bold text-[var(--warning-text)] px-1 self-center";

// The one dropdown both rows render. It shows what it was handed and invents
// nothing: the estimate's own value stays SELECTED even when it sits in the
// other collection, and it is named for what it is rather than dropped.
const ColorSelect = ({ colors, value, tierWord, what, busy, onPick, testid }) => (
  <>
    <select value={value || ""}
      disabled={busy}
      onChange={(e) => { if (e.target.value) onPick(e.target.value); }}
      title={`${tierWord} colors for ${what} — the same list the estimate page's ${what} dropdown reads. Picking one sets ${what} on this estimate; no money is written and no mark is moved`}
      className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[var(--border)] bg-white text-[var(--ink)]"
      data-testid={testid || "photo-takeoff-siding-color"}>
      <option value="">Pick a color</option>
      {colors.map((c) => <option key={c} value={c}>{c}</option>)}
      {value && !colors.includes(value)
        ? <option value={value}>{value} · on the estimate, not in this {tierWord} list</option>
        : null}
    </select>
    {value ? (
      <span className="text-[9px] font-bold text-[var(--ink-2)] self-center"
        data-testid={`${testid || "photo-takeoff-siding-color"}-current`}>
        {what.toUpperCase()}: {value}
      </span>
    ) : null}
  </>
);

// SEND-291 — A GATE BLOCKS THE LIST, NEVER THE PARENT'S OWN COLOUR. The
// product pick and the Standard | Arch tap decide WHICH NAMES THIS STEP MAY
// OFFER; they do not decide what the job already is. So when a gate stands and
// the parent carries a colour, the step SHOWS that colour — dead, because
// there is no list to change it from here — with both the reason and the
// parent's own line beside it. An empty parent still gets the plain refusal.
//
// SEND-292 (Howard ruled 2026-06) — **THE SAME ECHO ON THE PARENT'S OWN
// STEPS.** "A product or Standard / Arch tap blocks ONLY THE LIST. It never
// hides, clears, or delays a parent color that is already on the job." So the
// siding / B&B / shake zone step, the accessories rail and the soffit J row
// echo it too: a parent step shows the CHIP it always shows (its own field's
// label and value), a RIDER shows the parent's own line, and the gate's reason
// stands under either one.
const ParentEcho = ({ field, value, why, testid, what }) => (
  <>
    <select disabled value={value}
      className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[var(--border)] bg-[var(--surface-2)] text-[var(--ink-2)] opacity-70"
      data-testid={`${testid}-parent`}>
      <option value={value}>{value}</option>
    </select>
    {what ? (
      <span className="text-[9px] font-bold text-[var(--ink-2)] self-center"
        data-testid={`${testid}-parent-current`}>
        {what.toUpperCase()}: {value}
      </span>
    ) : null}
    <span className={NOTE} data-testid={`${testid}-parent-why`}>{why}</span>
    {field ? (
      <span className="text-[9px] text-[var(--muted)] w-full"
        data-testid={`${testid}-parent-note`}>
        {parentColorNote(field, value)}
      </span>
    ) : null}
  </>
);

// SEND-292 — HIS WORDING. With nothing on the parent the tap is the next thing
// to do, so it still says FIRST. With a colour already on the job the tap is
// only a filter, and the copy says exactly that.
const TAP_FIRST =
  "Tap Standard or Architectural first — the tap only filters this list";
const TAP_TO_FILTER = "Tap Standard or Architectural to filter other colors";

export const ProductColorRow = ({
  seriesKey, seriesLabel, fieldLabel, tier, value, busy = false, onPick, lp,
}) => {
  const what = fieldLabel || "This color";
  if (lp) {
    return (
      <LpColorSelect {...lp} busy={busy} what="Siding color"
        testid="photo-takeoff-lp-siding-color" />
    );
  }
  if (value === null) {
    return (
      <span className={NOTE} data-testid="photo-takeoff-siding-color-not-read">
        This estimate&apos;s {what} was not read — nothing is guessed here
      </span>
    );
  }
  // SEND-292 — every gate on this step routes through here: a colour on the
  // parent is SHOWN, selected and dead; an empty parent gets the bare note it
  // always got, under its own testid.
  const gate = (testid, why) => (value
    ? <ParentEcho value={value} why={why} what={what}
      testid="photo-takeoff-siding-color" />
    : <span className={NOTE} data-testid={testid}>{why}</span>);
  if (!seriesKey) {
    return gate("photo-takeoff-siding-color-no-product",
      "Pick the product first — a color belongs to that product's own list");
  }
  if (!hasColorList(seriesKey)) {
    return gate("photo-takeoff-siding-color-not-in-this-send",
      `${seriesLabel || "This product"} has no color list on this screen, and it was not handed another product's colors to stand in for one`);
  }
  if (!tier) {
    return gate("photo-takeoff-siding-color-no-tier",
      value ? TAP_TO_FILTER : TAP_FIRST);
  }
  const colors = colorsFor(seriesKey, tier);
  const tierWord = tier === "architectural" ? "Architectural" : "Standard";
  if (!colors.length) {
    return gate("photo-takeoff-siding-color-empty",
      `${seriesLabel || "This product"} has NO ${tierWord} color list — this list is empty and no color was invented for it`);
  }
  return (
    <ColorSelect colors={colors} value={value} tierWord={tierWord} what={what}
      busy={busy} onPick={onPick} />
  );
};

// SEND-262 — J, OUTSIDE CORNER, INSIDE CORNER AND FINISH TRIM. Four rails, ONE
// field: whichever one he is on writes ACCESSORIES COLOR, so the color he
// picked on the J shows SELECTED on the corners and on the finish trim.
export const AccessoryColorRow = ({
  tier, value, lines, busy = false, onPick, lp,
}) => {
  const what = COLOR_LABEL_FOR_FIELD[ACCESSORY_FIELD];
  if (lp) {
    return (
      <LpColorSelect {...lp} busy={busy} what="Outside corner color"
        testid="photo-takeoff-lp-osc-color" />
    );
  }
  if (value === null) {
    return (
      <span className={NOTE}
        data-testid="photo-takeoff-accessories-color-not-read">
        This estimate's {what} was not read — nothing is guessed here
      </span>
    );
  }
  // SEND-292 — OUTSIDE CORNERS is a parent too: a tap may filter its list,
  // never hide a colour the job already carries.
  const gate = (testid, why) => (value
    ? <ParentEcho value={value} why={why} what={what}
      testid="photo-takeoff-accessories-color" />
    : <span className={NOTE} data-testid={testid}>{why}</span>);
  if (!tier) {
    return gate("photo-takeoff-accessories-color-no-tier",
      value ? TAP_TO_FILTER : TAP_FIRST);
  }
  const colors = accessoryColorsFor(tier, lines);
  const tierWord = tier === "architectural" ? "Architectural" : "Standard";
  if (!colors.length) {
    return gate("photo-takeoff-accessories-color-empty",
      `This estimate's accessories list has NO ${tierWord} collection — this list is empty and no color was invented for it`);
  }
  return (
    <ColorSelect colors={colors} value={value} tierWord={tierWord} what={what}
      busy={busy} onPick={onPick}
      testid="photo-takeoff-accessories-color" />
  );
};

// SEND-264 — SOFFIT J ONLY, on the J rail. The estimate's SOFFIT & FASCIA
// COLOR, read from that field's OWN catalog (`SOFFIT_COLOR_GROUPS` — the same
// one the estimate page's SOFFIT & FASCIA COLOR dropdown renders), filtered by
// the tap and nothing else. No colour is defaulted and white is not assumed.
export const soffitColorsFor = (tier) => {
  const wantArch = tier === "architectural";
  return [...new Set(SOFFIT_COLOR_GROUPS
    .filter((g) => /architectural/i.test(g.label || "") === wantArch)
    .flatMap((g) => g.colors || []))];
};

export const SoffitJColorRow = ({ tier, value, busy = false, onPick }) => {
  const what = COLOR_LABEL_FOR_FIELD[SOFFIT_J_FIELD];
  if (value === null) {
    return (
      <span className={NOTE} data-testid="photo-takeoff-soffit-color-not-read">
        This estimate's {what} was not read — nothing is guessed here
      </span>
    );
  }
  // SEND-292 — SOFFIT / FASCIA is a parent too: set it and this row shows it
  // selected with no tap at all.
  const gate = (testid, why) => (value
    ? <ParentEcho value={value} why={why} what={what}
      testid="photo-takeoff-soffit-color" />
    : <span className={NOTE} data-testid={testid}>{why}</span>);
  if (!tier) {
    return gate("photo-takeoff-soffit-color-no-tier",
      value ? TAP_TO_FILTER : TAP_FIRST);
  }
  const colors = soffitColorsFor(tier);
  const tierWord = tier === "architectural" ? "Architectural" : "Standard";
  if (!colors.length) {
    return gate("photo-takeoff-soffit-color-empty",
      `The soffit and fascia list has NO ${tierWord} collection — this list is empty and no color was invented for it`);
  }
  return (
    <ColorSelect colors={colors} value={value} tierWord={tierWord} what={what}
      busy={busy} onPick={onPick}
      testid="photo-takeoff-soffit-color" />
  );
};

// SEND-270 (Howard ruled 2026-06) — THE SOFFIT STEP'S OWN COLOR ROW.
//
//   "That step asks TYPE / PRODUCT / KIND, but never COLOR. Use the SOFFIT
//    color list already in the catalog — NOT the siding / vinyl colors.
//    Filter the list by the PRODUCT already picked on this step. If that
//    product has no colors, show the picker disabled and say so. Do not
//    invent a default."
//
// The list is the SOFFIT palette, narrowed by the PRODUCT'S OWN CATALOG
// COLLECTIONS (`SOFFIT_COLOR_GROUPS_BY_SERIES`) and then by the tap. Charter
// Oak's soffit is two tiered catalog rows and carries both collections;
// Greenbriar and 2T are single UNTIERED rows — Greenbriar names the five
// Howard listed (SEND-271) and 2T names the one he listed, White (SEND-273),
// so neither takes a tap. A product whose row names NOTHING still gets a
// DISABLED picker with the reason beside it. Nothing here is a default, and
// the pick RIDES THE MARK: no quantity, no row, no money.
export const soffitSeriesGroups = (seriesKey) =>
  SOFFIT_COLOR_GROUPS_BY_SERIES[seriesKey] || [];

// SEND-271 — IS THIS PRODUCT TIERED? The catalog answers: Charter Oak's two
// rows name a Standard and an Architectural collection, so its groups carry
// those words; Greenbriar's single row names ONE untiered group. An untiered
// product needs NO TAP, because there is no second row to choose between.
export const soffitSeriesTiered = (seriesKey) =>
  soffitSeriesGroups(seriesKey).some(
    (g) => /standard|architectural/i.test(g.label || ""));

export const soffitSeriesColorsFor = (seriesKey, tier) => {
  const groups = soffitSeriesGroups(seriesKey);
  if (!soffitSeriesTiered(seriesKey)) {
    return [...new Set(groups.flatMap((g) => g.colors || []))];
  }
  const wantArch = tier === "architectural";
  return [...new Set(groups
    .filter((g) => /architectural/i.test(g.label || "") === wantArch)
    .flatMap((g) => g.colors || []))];
};

// SEND-293 — the same read on the soffit side: an UNTIERED product (Greenbriar,
// 2T) names no tier at all, and a colour in both or neither collection names
// none either.
export const soffitTierForColor = (seriesKey, color) => {
  if (!seriesKey || !color || !soffitSeriesTiered(seriesKey)) return null;
  const std = soffitSeriesColorsFor(seriesKey, "standard").includes(color);
  const arch = soffitSeriesColorsFor(seriesKey, "architectural").includes(color);
  if (std === arch) return null;
  return arch ? "architectural" : "standard";
};

// The disabled picker he asked for: it is SHOWN, it is DEAD, and the reason
// stands beside it. A hidden control cannot say why it is missing.
// SEND-272 — the same dead picker serves both J steps; each row names its own
// testid so a refusal on the J step cannot be read as one on the soffit step.
const DeadSelect = ({ why, testid = "photo-takeoff-soffit-step-color-disabled" }) => (
  <>
    <select disabled value=""
      className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[var(--border)] bg-[var(--surface-2)] text-[var(--muted)] opacity-60"
      data-testid={testid}>
      <option value="">No color list</option>
    </select>
    <span className={NOTE} data-testid={`${testid}-why`}>{why}</span>
  </>
);

// SEND-311 (Howard ruled 2026-06) — THE LP COLOR PICKER. On an lp_smart
// estimate each of the four walk pickers shows the ExpertFinish colors THE
// ROW IT LANDS ON carries, read from the server (the dealer-verified matrix).
// No vinyl colors, nothing invented: an unpicked product shows the dead picker
// and says "pick the product first"; a colorless row names itself and offers
// nothing; and only `available` colors are listed (GAP / unsupported are
// withheld by the server, never shown). The pick rides the mark, a label only.
export const LpColorSelect = ({
  hasProduct, colors = [], row, refusal, value, busy = false, onPick,
  what = "This color", testid = "photo-takeoff-lp-color",
}) => {
  if (!hasProduct) {
    return (
      <DeadSelect testid={testid}
        why="Pick the product first — an LP color belongs to the row it lands on, and none is shown until you do" />
    );
  }
  if (refusal || !colors.length) {
    return (
      <DeadSelect testid={testid}
        why={refusal || `${row ? `${row} carries` : "This LP row carries"} no ExpertFinish color in the catalog — reported by name, none invented`} />
    );
  }
  return (
    <ColorSelect colors={colors} value={value || ""} tierWord="LP SmartSide"
      what={what} busy={busy} onPick={onPick} testid={testid} />
  );
};


export const SoffitStepColorRow = ({
  seriesKey, seriesLabel, tier, value, busy = false, onPick, lp,
}) => {
  if (lp) {
    return (
      <LpColorSelect {...lp} busy={busy} what="Soffit color"
        testid="photo-takeoff-lp-soffit-color" />
    );
  }
  if (value === null) {
    return (
      <span className={NOTE} data-testid="photo-takeoff-soffit-step-color-not-read">
        This estimate&apos;s {COLOR_LABEL_FOR_FIELD[SOFFIT_J_FIELD]} was not read — nothing is guessed here
      </span>
    );
  }
  // SEND-291 — each gate names its own reason; a gate with a colour on the
  // parent ECHOES that colour instead of pretending the job has none.
  const gated = (why) => (value
    ? <ParentEcho field={SOFFIT_J_FIELD} value={value} why={why}
      testid="photo-takeoff-soffit-step-color" />
    : <DeadSelect why={why} />);
  if (!seriesKey) {
    return gated("Pick the soffit product first — a color belongs to that product's own list, and no list is guessed for it");
  }
  if (!soffitSeriesGroups(seriesKey).length) {
    return gated(`${seriesLabel || "This product"} names no color collection in the Vinyl Soffit with Siding catalog — no color is offered and none was invented`);
  }
  // SEND-271 — the tap is a gate ONLY where the catalog has two rows to
  // choose between. Greenbriar's five ride its one row and need no tap.
  const tiered = soffitSeriesTiered(seriesKey);
  if (tiered && !tier) {
    return gated(value ? TAP_TO_FILTER
      : "Tap Standard or Architectural first — the tap only filters this product's own list");
  }
  const colors = soffitSeriesColorsFor(seriesKey, tier);
  const tierWord = !tiered
    ? (seriesLabel || "This product")
    : (tier === "architectural" ? "Architectural" : "Standard");
  if (!colors.length) {
    return gated(`${seriesLabel || "This product"} has NO ${tierWord} soffit collection in the catalog — this list is empty and no color was invented for it`);
  }
  return (
    <>
      <ColorSelect colors={colors} value={value || ""} tierWord={tierWord}
        what="Soffit color" busy={busy} onPick={onPick}
        testid="photo-takeoff-soffit-step-color" />
      {/* SEND-291 — the soffit step RIDES SOFFIT / FASCIA. The list stays this
          product's own; the value read and written is the parent field. */}
      <span className="text-[9px] text-[var(--muted)] w-full"
        data-testid="photo-takeoff-soffit-step-color-note">
        {parentColorNote(SOFFIT_J_FIELD, value)}
      </span>
    </>
  );
};

// SEND-272 (Howard ruled 2026-06) — COLOR ON BOTH J STEPS, FROM THE PRODUCT'S
// OWN CATALOG.
//
//   "COLOR uses the J-channel catalog already in the app, filtered by the
//    product on that step. Do not scrape Alside. Do not invent names. If that
//    product has no color rows: disabled picker and the reason in words. No
//    default color."
//
// The map is SEND-264's, unchanged and now read for the LIST as well as the
// label: siding J → the SIDING palette of the brands on THIS ESTIMATE'S OWN
// LINES (the same function the estimate page's siding dropdown calls), B&B J →
// BOARD & BATTEN, shake J → SHAKE, soffit J → SOFFIT & FASCIA. Each is tiered,
// so the STANDARD | ARCH tap above filters it and does nothing else. The pick
// RIDES THE J MARK: no quantity, no row, no money, no estimate field.
export const J_COLOR_LABEL = {
  siding: "Siding color",
  bb: "Board & batten color",
  shake: "Shake color",
  soffit: "Soffit & fascia color",
};

// SEND-291 (Howard ruled 2026-06) — **A RIDER READS ITS PARENT.** "Every walk
// step that rides a parent READS that parent. It does not start empty." So the
// J colour is NOT a mark-local name any more: siding J / B&B J / shake J read
// the SIDING family's own field, soffit J reads SOFFIT & FASCIA, and a pick on
// the step writes THAT PARENT — the one field the JOB COLORS row writes — so
// every other rider and every photo shows it at once. No later step keeps a
// disconnected colour of its own, and no per-wall colour exists anywhere.
export const J_PARENT_FIELD = {
  siding: "siding_color",
  bb: "board_batten_color",
  shake: "shake_color",
  soffit: "soffit_fascia_color",
};

// THE PARENT'S OWN WORD, for the one line of copy under every rider. His
// wording, per parent: "same as siding", "it sets siding on every photo".
export const PARENT_WORD = {
  siding_color: "siding",
  board_batten_color: "board & batten",
  shake_color: "shake",
  soffit_fascia_color: "soffit and fascia",
  accessories_color: "outside corners",
};

// SEND-291 — HIS COPY, EXACTLY. The old "Optional. Skip it and the J mark
// carries no color — nothing is defaulted." is DELETED: it described a
// disconnected pick that no longer exists.
export const parentColorNote = (field, value) => {
  const word = PARENT_WORD[field] || "this parent";
  const label = COLOR_LABEL_FOR_FIELD[field] || "Color";
  return value
    ? `${label}: ${value} — same as ${word}.`
    : `No ${word} color yet. Pick one and it sets ${word} on every photo.`;
};

// SEND-291 — THE RIDERS WITH NO CONTROL AT ALL until now: fascia, the band
// board's MATCH chip, finish trim and inside corners. Each one SHOWS the
// parent it rides, already set, and a pick writes that parent. The list is the
// parent's own palette with its own headers — no tap gate, because the tap
// filters a step's product list and these steps have no product of their own.
export const RiderColorRow = ({
  field, groups, value, busy = false, onPick, testid, lp,
}) => {
  const label = COLOR_LABEL_FOR_FIELD[field] || "Color";
  if (lp) {
    const { what, testid: lpTestid, ...rest } = lp;
    return (
      <LpColorSelect {...rest} busy={busy}
        what={what || "Trim board color"}
        testid={lpTestid || "photo-takeoff-lp-trim-color"} />
    );
  }
  if (value === null) {
    return (
      <span className={NOTE} data-testid={`${testid}-not-read`}>
        This estimate&apos;s {label} was not read — nothing is guessed here
      </span>
    );
  }
  const flat = (groups || []).flatMap((g) => g.colors || []);
  return (
    <>
      <select value={value || ""} disabled={busy}
        onChange={(e) => { if (e.target.value) onPick(e.target.value); }}
        title={`${label} for the whole job — this step RIDES it. Picking one here sets ${PARENT_WORD[field] || "that parent"} on every photo; no mark is moved and no money is written`}
        className="px-2 py-1 text-[10px] font-bold uppercase tracking-wider border border-[var(--border)] bg-white text-[var(--ink)]"
        data-testid={testid}>
        <option value="">Pick a color</option>
        {(groups || []).map((g) => (
          <optgroup key={g.label} label={g.label}>
            {(g.colors || []).map((c) => <option key={c} value={c}>{c}</option>)}
          </optgroup>
        ))}
        {value && !flat.includes(value)
          ? <option value={value}>{value} · on the estimate, not in this list</option>
          : null}
      </select>
      <span className="text-[9px] text-[var(--muted)] w-full"
        data-testid={`${testid}-note`}>
        {parentColorNote(field, value)}
      </span>
    </>
  );
};

// SEND-293 (Howard ruled 2026-06) — **THE COLOUR DECIDES THE TAP, THE WAY IT
// DECIDES THE LIST.** The colour he set lives in one product's ARCHITECTURAL
// collection, so a job wearing it is an Architectural job on that product —
// and the step reading that colour must not sit on STANDARD saying
// the colour is "not in this list". The tier comes from THE CATALOG'S OWN
// COLLECTIONS and nowhere else. A colour in BOTH collections, or in NEITHER,
// names NO tier: an ambiguous colour is not evidence of a tap.
export const tierForColor = (seriesKey, color) => {
  if (!seriesKey || !color) return null;
  const std = colorsFor(seriesKey, "standard").includes(color);
  const arch = colorsFor(seriesKey, "architectural").includes(color);
  if (std === arch) return null;
  return arch ? "architectural" : "standard";
};

export const jProductGroups = (product, lines) => {
  if (product === "siding") return vinylSidingColorGroupsForEstimate(lines || []);
  if (product === "bb") return BB_GROUPS;
  if (product === "shake") return SHAKE_GROUPS;
  if (product === "soffit") return SOFFIT_COLOR_GROUPS;
  return [];
};

export const jColorsFor = (product, tier, lines) => {
  const wantArch = tier === "architectural";
  // One NAME once: the siding catalog concatenates every active brand's
  // collections, so a name can appear in several. The set is identical.
  return [...new Set(jProductGroups(product, lines)
    .filter((g) => /architectural/i.test(g.label || "") === wantArch)
    .flatMap((g) => g.colors || []))];
};

export const JStepColorRow = ({
  product, productLabel, tier, value, lines, busy = false, onPick,
}) => {
  // SEND-291 — the parent is the ESTIMATE'S field. If it was not read, say so:
  // an unread field is not an empty one.
  if (value === null) {
    return (
      <span className={NOTE} data-testid="photo-takeoff-j-step-color-not-read">
        This estimate&apos;s {COLOR_LABEL_FOR_FIELD[J_PARENT_FIELD[product]] || "color"} was not read — nothing is guessed here
      </span>
    );
  }
  // SEND-291 — the same rule on the J: a gate blocks the LIST, never the
  // parent's own colour.
  const gated = (why) => (value
    ? <ParentEcho field={J_PARENT_FIELD[product]} value={value} why={why}
      testid="photo-takeoff-j-step-color" />
    : <DeadSelect testid="photo-takeoff-j-step-color-disabled" why={why} />);
  if (!product) {
    return gated("Pick the J product first — a color belongs to that product's own list, and no list is guessed for it");
  }
  if (!jProductGroups(product, lines).length) {
    return gated(`${productLabel || "This product"} has no color rows in the catalog on this estimate — no color is offered and none was invented`);
  }
  if (!tier) {
    return gated(value ? TAP_TO_FILTER
      : "Tap Standard or Architectural first — the tap only filters this product's own list");
  }
  const colors = jColorsFor(product, tier, lines);
  const tierWord = tier === "architectural" ? "Architectural" : "Standard";
  if (!colors.length) {
    return gated(`${productLabel || "This product"} has NO ${tierWord} collection in the catalog — this list is empty and no color was invented for it`);
  }
  return (
    <>
      <ColorSelect colors={colors} value={value || ""} tierWord={tierWord}
        what={J_COLOR_LABEL[product] || "J color"} busy={busy} onPick={onPick}
        testid="photo-takeoff-j-step-color" />
      {/* SEND-291 — THE PARENT'S OWN LINE, not an "optional" one. The J does
          not own this colour: it reads the family it trims. */}
      <span className="text-[9px] text-[var(--muted)] w-full"
        data-testid="photo-takeoff-j-step-color-note">
        {parentColorNote(J_PARENT_FIELD[product], value)}
      </span>
    </>
  );
};

export default ProductColorRow;